import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Container, Paper, Button } from '@mui/material';
import axios from 'axios';
import { io } from 'socket.io-client';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TestResults from './TestResults';

// Reference to the host IP instead of localhost
const API_URL = process.env.REACT_APP_API_URL || 'https://45.225.80.187:8010';
console.log("TestPage using API URL:", API_URL);

let socket;

const TestPage = () => {
  const { id } = useParams(); // Get the test ID from URL
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);

  // Connect to Socket.IO when component mounts
  useEffect(() => {
    // Usar a mesma URL base da API, mas removendo /api se presente para WebSocket
    let socketUrl = API_URL.replace('/api', '');
    
    console.log('TestPage: Connecting to Socket.IO at', socketUrl);
    
    socket = io(socketUrl, {
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      timeout: 20000,
      autoConnect: true,
      withCredentials: false,
    });
    
    socket.on('connect', () => {
      console.log('TestPage: Connected to WebSocket', socket.id);
      setSocketConnected(true);
      // Send ping to test connection
      socket.emit('ping', { message: 'Hello from client' }, (response) => {
        console.log('TestPage: Ping response:', response);
      });
    });
    
    socket.on('connect_confirmed', (data) => {
      console.log('TestPage: Server confirmed connection:', data);
    });
    
    socket.on('disconnect', () => {
      console.log('TestPage: Disconnected from WebSocket');
      setSocketConnected(false);
    });
    
    socket.on('connect_error', (error) => {
      console.error('TestPage: Socket connection error:', error);
    });
    
    socket.on('test_update', (data) => {
      console.log('TestPage: Received test_update event:', data);
      if (data.test_id === id) {
        console.log(`TestPage: Update matches current test ID: ${id}`, data);
        
        if (data.status) {
          console.log(`TestPage: Test status changed to: ${data.status}`);
          
          if (data.status === 'processing') {
            setLoading(true);
            setQueueStatus({
              position: 0,
              estimated_wait_time: 0,
              total_in_queue: 0,
              status: 'processing'
            });
          } else if (data.status === 'success') {
            setLoading(false);
            setQueueStatus(null);
            console.log('TestPage: Setting test results:', data.result);
            setTestResults(data.result);
          } else if (data.status === 'error') {
            setLoading(false);
            setQueueStatus(null);
            setError(data.error || 'An error occurred during testing');
          }
        }
      }
    });
    
    return () => {
      if (socket) socket.disconnect();
    };
  }, [id]);
  
  // Fetch test data when component mounts
  useEffect(() => {
    const fetchTestData = async () => {
      try {
        console.log(`TestPage: Fetching test data for ID: ${id}`);
        const response = await axios.get(`${API_URL}/rtsp/test/${id}`);
        console.log('TestPage: Test data response:', response.data);
        
        if (response.data) {
          setTestResults(response.data);
          setLoading(false);
        }
      } catch (err) {
        console.error('TestPage: Error fetching test data:', err);
        
        // Check if we need to poll queue status instead
        try {
          console.log(`TestPage: Checking queue status for ID: ${id}`);
          const queueResponse = await axios.get(`${API_URL}/rtsp/queue/${id}`);
          console.log('TestPage: Queue status response:', queueResponse.data);
          
          if (queueResponse.data) {
            setQueueStatus(queueResponse.data);
            if (queueResponse.data.status !== 'processing') {
              setLoading(false);
            }
          }
        } catch (queueErr) {
          console.error('TestPage: Error fetching queue status:', queueErr);
          setError('Error loading test results. The test may not exist or has been removed.');
          setLoading(false);
        }
      }
    };
    
    fetchTestData();
    
    // Set up polling for queue status if needed
    const interval = setInterval(async () => {
      if (loading) {
        try {
          const queueResponse = await axios.get(`${API_URL}/rtsp/queue/${id}`);
          setQueueStatus(queueResponse.data);
          
          if (queueResponse.data.status === 'success') {
            // If status is success, fetch full test results
            const testResponse = await axios.get(`${API_URL}/rtsp/test/${id}`);
            setTestResults(testResponse.data);
            setLoading(false);
          } else if (queueResponse.data.status === 'error') {
            setError('An error occurred while processing the RTSP stream');
            setLoading(false);
          }
        } catch (err) {
          console.error('TestPage: Error during polling:', err);
        }
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [id, loading]);
  
  const renderQueueStatus = () => {
    if (!queueStatus) return null;
    
    return (
      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
        {queueStatus.position === 0 ? (
          'Your RTSP stream is being processed...'
        ) : (
          `Position in queue: ${queueStatus.position} | Estimated wait time: ${queueStatus.estimated_wait_time} seconds`
        )}
      </Alert>
    );
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
            <Button 
              component={Link} 
              to="/" 
              startIcon={<ArrowBackIcon />} 
              variant="outlined" 
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <Typography variant="h5" component="h1">
              RTSP Test Results
            </Typography>
          </Box>
          
          {!socketConnected && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              WebSocket connection not established. Real-time updates may not work.
            </Alert>
          )}
          
          {loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Loading test results...
              </Typography>
              {renderQueueStatus()}
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          
          {!loading && testResults && (
            <TestResults results={testResults} />
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default TestPage;